import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AwardBookRecommendation {
  title: string;
  author: string;
  reason: string;
  match_score: number;
  bookId?: string;
  coverUrl?: string;
  awardName?: string;
  awardYear?: number;
}

interface CachedAwardRecommendation {
  id: string;
  user_id: string;
  book_id: string | null;
  book_title: string;
  book_author: string | null;
  cover_url: string | null;
  reason: string | null;
  recommendation_type: string | null;
  confidence_score: number | null;
  match_details: string | null;
  source_books: any;
  status: string;
  created_at: string;
  expires_at: string;
}

const CACHE_KEY = 'cachedAwardRecommendations';
const CACHE_DURATION_MS = 1000 * 60 * 60 * 24; // 24 hours

export const useCachedAwardRecommendations = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [CACHE_KEY],
    queryFn: async (): Promise<AwardBookRecommendation[]> => {
      console.log('🏆 Checking for cached award book recommendations...');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Try to fetch from cache first
      const { data: cachedRecs, error: cacheError } = await supabase
        .from('user_cached_recommendations')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('recommendation_type', 'award')
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('confidence_score', { ascending: false })
        .limit(10);

      if (!cacheError && cachedRecs && cachedRecs.length >= 4) {
        console.log(`✅ Found ${cachedRecs.length} cached award recommendations`);
        return cachedRecs.map((rec: CachedAwardRecommendation) => ({
          title: rec.book_title,
          author: rec.book_author || 'Unknown',
          reason: rec.reason || '',
          match_score: rec.confidence_score || 80,
          bookId: rec.book_id || undefined,
          coverUrl: rec.cover_url || undefined,
          awardName: rec.match_details?.split(' ')?.[0],
          awardYear: parseInt(rec.match_details?.match(/\d{4}/)?.[0] || '') || undefined,
        }));
      }

      // No cache or insufficient cached recs - fetch fresh and cache
      console.log('🏆 Fetching fresh award book recommendations...');
      
      const { data, error } = await supabase.functions.invoke('recommend-award-books', {
        body: {},
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('❌ Error fetching award recommendations:', error);
        throw error;
      }

      if (!data || !data.recommendations || data.recommendations.length === 0) {
        console.warn('⚠️ No award recommendations returned');
        return [];
      }

      console.log(`✅ Got ${data.recommendations.length} fresh award book recommendations`);

      // Cache the recommendations
      const recsToCache = data.recommendations.map((rec: AwardBookRecommendation) => ({
        user_id: session.user.id,
        book_id: rec.bookId || null,
        book_title: rec.title,
        book_author: rec.author,
        cover_url: rec.coverUrl || null,
        reason: rec.reason,
        recommendation_type: 'award',
        confidence_score: rec.match_score,
        match_details: rec.awardName && rec.awardYear 
          ? `${rec.awardName} ${rec.awardYear}` 
          : rec.awardName || null,
        source_books: [],
        status: 'pending',
        expires_at: new Date(Date.now() + CACHE_DURATION_MS).toISOString(),
      }));

      // Clear old award recommendations before inserting new ones
      await supabase
        .from('user_cached_recommendations')
        .delete()
        .eq('user_id', session.user.id)
        .eq('recommendation_type', 'award');

      // Insert new cached recommendations
      const { error: insertError } = await supabase
        .from('user_cached_recommendations')
        .insert(recsToCache);

      if (insertError) {
        console.error('❌ Error caching award recommendations:', insertError);
      } else {
        console.log(`✅ Cached ${recsToCache.length} award recommendations`);
      }

      return data.recommendations;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - use cache
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: (prev: AwardBookRecommendation[] | undefined) => prev,
  });

  const refresh = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Clear cached award recommendations
    await supabase
      .from('user_cached_recommendations')
      .delete()
      .eq('user_id', session.user.id)
      .eq('recommendation_type', 'award');

    // Refetch
    queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
  };

  return {
    ...query,
    refresh,
  };
};
